import ja_base from '../locales/ja/base.json';
import ja_overlays from '../locales/ja/overlays.json';
import ja_tutorials from '../locales/ja/tutorials.json';
import en_base from '../locales/en/base.json';
import en_overlays from '../locales/en/overlays.json';
import en_tutorials from '../locales/en/tutorials.json';
import { getEffectiveScale as getEffectiveScaleAuthoritative } from '../gameLogic/gameUtils.js';

// 各言語のデータを統合するヘルパー関数
const bundleLocale = (base, overlays, tutorials) => {
    return {
        ...base,
        UI: {
            ...base.UI,
            ...overlays,
            TUTORIAL: tutorials
        }
    };
};

const ja = bundleLocale(ja_base, ja_overlays, ja_tutorials);
const en = bundleLocale(en_base, en_overlays, en_tutorials);

const locales = {
    ja: ja,
    en: en
};

let currentLang = 'ja';

/**
 * 現在の言語を設定します
 */
export const setLanguage = (lang) => {
    if (locales[lang]) {
        currentLang = lang;
    }
};

/**
 * 現在の言語を取得します
 */
export const getLanguage = () => currentLang;

/**
 * カードの翻訳データを取得します
 */
export const getCardLoc = (cardId, lang = currentLang) => {
    // 数値IDで引く（"1"〜"95"）
    return locales[lang]?.[cardId?.toString()] || locales['ja']?.[cardId?.toString()] || { name: `CARD_${cardId}`, description: '', flavor: '' };
};

/**
 * カードの翻訳名を取得します
 */
export const getCardName = (cardId, lang = currentLang) => {
    return getCardLoc(cardId, lang).name;
};

/**
 * カードタイプの表示名を取得します
 */
export const getCardTypeName = (typeId, lang = currentLang) => {
    return locales[lang]?.UI?.CARD_TYPES?.[typeId?.toString()] || locales['ja']?.UI?.CARD_TYPES?.[typeId?.toString()] || `TYPE_${typeId}`;
};

/**
 * UIの翻訳データを取得します
 * getUILoc('BUTTONS.CONFIRM') -> locales.ja.UI.BUTTONS.CONFIRM
 * getUILoc('PRESET_DECKS.DECK_0.NAME') -> locales.ja.PRESET_DECKS.DECK_0.NAME
 */
export const getUILoc = (key, params = {}, lang = currentLang) => {
    if (!key) return '';
    const keys = key.split('.');
    
    // 1. まず UI セクションの下を探す
    let result = locales[lang]?.UI;
    for (const k of keys) {
        result = result?.[k];
    }
    
    // 2. 見つからない場合はルートから探す（PRESET_DECKSなど）
    if (result === undefined) {
        result = locales[lang];
        for (const k of keys) {
            result = result?.[k];
        }
    }
    
    // 3. 指定言語で見つからない場合は日本語でフォールバック
    if (result === undefined || result === key) {
        // 日本語の UI セクション
        result = locales['ja']?.UI;
        for (const k of keys) {
            result = result?.[k];
        }
        
        // 日本語のルート
        if (result === undefined) {
            result = locales['ja'];
            for (const k of keys) {
                result = result?.[k];
            }
        }
    }
    
    if (typeof result === 'string') {
        // パラメータによる置換
        let finalResult = result;
        Object.keys(params).forEach(p => {
            const placeholder = `{${p}}`;
            while (finalResult.includes(placeholder)) {
                finalResult = finalResult.replace(placeholder, params[p]);
            }
        });
        return finalResult;
    }
    
    return result || key;
};

/**
 * プレイヤーの表示名を取得します
 */
export const getPlayerName = (playerType, lang = currentLang) => {
    return locales[lang]?.UI?.PLAYER_NAME?.[playerType] || locales['ja']?.UI?.PLAYER_NAME?.[playerType] || playerType;
};

/**
 * カードタイプに基づくCSSクラス用文字列を取得します
 */
const TYPE_MAP = {
    1: 'wealth',
    2: 'event',
    3: 'ideology'
};

export const getCardTypeClass = (typeId) => {
    return TYPE_MAP[typeId] || `type-${typeId}`;
};

/**
 * カードIDの配列を枚数ごとに集計します
 * @param {number[]} cardIds 
 * @returns {{id: number, count: number}[]}
 */
export const groupCards = (cardIds) => {
    const counts = {};
    cardIds.forEach(id => {
        counts[id] = (counts[id] || 0) + 1;
    });
    return Object.keys(counts).map(id => ({
        id: parseInt(id),
        count: counts[id]
    })).sort((a, b) => a.id - b.id);
};

/**
 * プレイヤーの有効規模を計算します
 * gameUtilsの実装を再利用
 */
export const getEffectiveScale = (player, gameState) => {
    return getEffectiveScaleAuthoritative(player, gameState);
};
